import { create } from 'zustand';

/**
 * Calendar store — persisted navigation + selection state.
 *
 * Gesture logic (click / shift+click / mouse-drag / touch-drag) lives in
 * `src/hooks/useCalendarSelection.ts`, which is the single source of truth
 * for how user inputs mutate `selectedDates`. This store just holds the
 * resulting values so that peripheral surfaces (UserProfile danger-zone,
 * header navigation, quick-nav) can read/clear them without coupling to
 * that hook.
 */
interface CalendarState {
  currentDate: Date;
  selectedDates: string[];
  setSelectedDates: (dates: string[] | ((prev: string[]) => string[])) => void;
  clearSelection: () => void;
  setCurrentDate: (d: Date | ((prev: Date) => Date)) => void;
  navigateDate: (dir: number, viewMode: "week" | "month" | "quarter") => void;
  goToday: () => void;
  handleQuickNavigate: (year: number, month: number) => void;
}

export const useCalendarStore = create<CalendarState>((set) => ({
  currentDate: new Date(),
  selectedDates: [],

  setSelectedDates: (d) => set(
    (state) => ({ selectedDates: typeof d === 'function' ? d(state.selectedDates) : d })
  ),
  clearSelection: () => set({ selectedDates: [] }),

  setCurrentDate: (d) => set(
    (state) => ({ currentDate: typeof d === 'function' ? d(state.currentDate) : d })
  ),
  goToday: () => set({ currentDate: new Date() }),
  navigateDate: (dir, viewMode) => set((state) => {
    // Clone and normalise to day-1 before shifting months/quarters
    // so that JS never overflows e.g. March-31 + 1 month → May-1.
    const prev = state.currentDate;
    const d = new Date(prev.getFullYear(), prev.getMonth(), 1);
    if (viewMode === "week") {
      // For weeks keep the original date and just shift by 7 days
      const w = new Date(prev);
      w.setDate(w.getDate() + dir * 7);
      return { currentDate: w };
    }
    if (viewMode === "month") d.setMonth(d.getMonth() + dir);
    else d.setMonth(d.getMonth() + dir * 3);
    return { currentDate: d };
  }),
  handleQuickNavigate: (year, month) => set((state) => {
    const d = new Date(state.currentDate);
    d.setFullYear(year);
    d.setMonth(month);
    return { currentDate: d };
  }),
}));
